import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { proxyAPI } from "../../proxyAPI.ts";
import { createEditorState } from "../../helpers/index";

// Stable empty array — reused as fallback in selectors to avoid new-reference warnings
const EMPTY_ARRAY = Object.freeze([]);

// Define the initial state
const initialState = {
  Workflows: [],
  EventHandlers: [],
  MessageTemplates: [],
  MessageConfigs: [],
  InputArguments: [],
  BusinessHours: [],
  Calendars: [],
};

const getAction = (state, actionUUID) =>
  state.EventHandlers.map((e) => e.Rules)
    .flat(1)
    .map((r) => r.Actions)
    .flat(1)
    .find((a) => a.UUID === actionUUID);

export const createNewMessageConfig = ({ UUID, Label, commsSettings }) => ({
  UUID: UUID,
  Label: Label || "New message config",
  EmailResponsesTo: "",
  EmailFrom: commsSettings.EmailFrom,
  Subject: commsSettings.EmailSubject,
  DynamicHeader: commsSettings.DynamicHeader,
  EmailSignature: commsSettings.EmailSignature,
  Validity: 0,
  IsOpenTicket: false,
  CampaignName: "",
  EditorStateEmailFrom: createEditorState(commsSettings.EmailFrom),
  EditorStateSubject: createEditorState(commsSettings.EmailSubject),
  EditorStateEmailSignature: createEditorState(commsSettings.EmailSignature),
});

export const saveProject = createAsyncThunk(
  "project/saveProject",
  async ({ navigate, engine }, { getState, rejectWithValue }) => {
    const state = getState();

    try {
      await Promise.all(
        state.sendLists.map((sl) =>
          proxyAPI({
            prefix: "contacts/v1",
            method: "POST",
            url: "/upsert-send-list",
            body: sl,
          })
        )
      );

      const data = await proxyAPI({
        prefix: "logic/workflows/api",
        url: `project${state.project.ID ? `/${state.project.ID}` : ``}`,
        method: "POST",
        body: {
          ...state.project,
          // remove unused event handlers
          EventHandlers: state.project.EventHandlers.filter((eh) =>
            state.project.Workflows.find((n) =>
              n.EventListeners?.find((el) => el.EventHandlerUUID === eh.UUID)
            )
          ),
          ComponentConfig: {
            Layout: engine.model.serialize(),
          },
        },
      });
      if (data.ID) {
        if (!state.project.ID) {
          navigate(`/library/logic/builder/${data.ID}`);
        }
        return data;
      }
      return rejectWithValue(data.message);
    } catch (error) {
      console.error("Error:", error);
      return rejectWithValue(error.toString());
    }
  }
);

export const projectSlice = createSlice({
  name: "project",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setProjectData: (state, action) => ({ ...initialState, ...action.payload }),
    closeProject: () => initialState,
    setProjectMessageConfigUUID: (state, action) => {
      state.MessageConfigUUID = action.payload;
    },
    setWorkflow: (state, action) => ({
      ...state,
      Workflows: state.Workflows.map((wf) => {
        if (wf.UUID === action.payload.UUID) {
          return action.payload;
        }
        return wf;
      }),
    }),
    addNewWorkflow: (state, action) => {
      if (!state.Workflows.some((wf) => wf.UUID === action.payload.UUID)) {
        state.Workflows.push(action.payload);
      }
    },
    setEntryWorkflow: (state, action) => ({
      ...state,
      Workflows: state.Workflows.map((wf) => {
        if (wf.UUID === action.payload) {
          return { ...wf, IsEntrypoint: true };
        }
        return { ...wf, IsEntrypoint: false };
      }),
    }),
    deleteWorkflow: (state, action) => ({
      ...state,
      Workflows: state.Workflows.filter((wf) => wf.UUID !== action.payload),
    }),
    setEventHandler: (state, action) => ({
      ...state,
      EventHandlers: state.EventHandlers.map((eh) => {
        if (eh.UUID === action.payload.UUID) {
          return action.payload;
        }
        return eh;
      }),
    }),
    setEventHandlers: (state, action) => ({
      ...state,
      EventHandlers: action.payload,
    }),
    editAction: (state, action) => {
      const act = getAction(state, action.payload.actionUUID);
      act[action.payload.field] = action.payload.value;
    },
    setMessageTemplates: (state, action) => ({
      ...state,
      MessageTemplates: action.payload,
    }),
    setMessageTemplate: (state, action) => ({
      ...state,
      MessageTemplates: state.MessageTemplates.map((mt) => {
        if (mt.UUID === action.payload.UUID) {
          return action.payload;
        }
        return mt;
      }),
    }),
    deleteMessageTeplate: (state, action) => ({
      ...state,
      MessageTemplates: state.MessageTemplates.filter(
        (mt) => mt.UUID !== action.payload
      ),
    }),
    addNewMessageTemplate: (state, action) => {
      const newMessage = {
        UUID: action.payload.UUID,
        Label: action.payload.Label || "New message",
        Text: action.payload.Text || "",
        EditorState: createEditorState(action.payload.Text),
      };
      state.MessageTemplates.push(newMessage);
    },
    setMessageConfigs: (state, action) => ({
      ...state,
      MessageConfigs: action.payload,
    }),
    setMessageConfig: (state, action) => ({
      ...state,
      MessageConfigs: state.MessageConfigs.map((mt) => {
        if (mt.UUID === action.payload.UUID) {
          return action.payload;
        }
        return mt;
      }),
    }),
    deleteMessageConfig: (state, action) => ({
      ...state,
      MessageConfigs: state.MessageConfigs.filter(
        (mt) => mt.UUID !== action.payload
      ),
    }),
    addNewMessageConfig: (state, action) => {
      const newMessageConfig = createNewMessageConfig(action.payload);
      state.MessageConfigs.push(newMessageConfig);
    },
    setInputArguments: (state, action) => ({
      ...state,
      InputArguments: action.payload,
    }),
    setInputArgument: (state, action) => ({
      ...state,
      InputArguments: state.InputArguments.map((ia) => {
        if (ia.UUID === action.payload.UUID) {
          return action.payload;
        }
        return ia;
      }),
    }),
    deleteInputArgument: (state, action) => {
      const deletedIA = state.InputArguments.find(
        (ia) => ia.UUID === action.payload
      );
      return {
        ...state,
        InputArguments: state.InputArguments?.filter(
          (ia) => ia.UUID !== action.payload
        )
          ?.sort((a, b) => a.Order - b.Order)
          ?.map((ia) => {
            if (ia.VisibilityField === deletedIA.Field) {
              return {
                ...ia,
                VisibilityField: null,
                VisibilityValues: null,
                VisibilityLogical: null,
              };
            }
            return ia;
          })
          ?.map((ia, index) => ({ ...ia, Order: index })),
        VariableGroups: state.VariableGroups?.map((ia) => {
          if (ia.VisibilityField === deletedIA.Field) {
            return {
              ...ia,
              VisibilityField: null,
              VisibilityValues: null,
              VisibilityLogical: null,
            };
          }
          return ia;
        }),
      };
    },
    addNewInputArgument: (state, action) => {
      const newInputArgument = {
        UUID: action.payload.UUID,
        Label: "New variable",
        Format: "short-text",
        Order: state.InputArguments ? state.InputArguments.length : 0,
        ...action.payload,
      };
      if (!state.InputArguments) state.InputArguments = [];
      state.InputArguments.push(newInputArgument);
    },
    moveInputArgumentUp: (state, action) => {
      const index = state.InputArguments.findIndex(
        (ia) => ia.UUID === action.payload
      );
      if (index > 0) {
        const newInputArguments = [...state.InputArguments];
        [newInputArguments[index - 1], newInputArguments[index]] = [
          newInputArguments[index],
          newInputArguments[index - 1],
        ];
        return {
          ...state,
          InputArguments: newInputArguments.map((ia, idx) => ({
            ...ia,
            Order: idx,
          })),
        };
      }
    },
    moveInputArgumentDown: (state, action) => {
      const index = state.InputArguments.findIndex(
        (ia) => ia.UUID === action.payload
      );
      if (index < state.InputArguments.length - 1) {
        const newInputArguments = [...state.InputArguments];
        [newInputArguments[index + 1], newInputArguments[index]] = [
          newInputArguments[index],
          newInputArguments[index + 1],
        ];
        return {
          ...state,
          InputArguments: newInputArguments.map((ia, idx) => ({
            ...ia,
            Order: idx,
          })),
        };
      }
    },
    moveInputArgument: (state, action) => {
      const { sourceIndex, destinationIndex } = action.payload;
      const newInputArguments = [...state.InputArguments];
      const [movedArgument] = newInputArguments.splice(sourceIndex, 1);
      newInputArguments.splice(
        sourceIndex > destinationIndex
          ? destinationIndex
          : destinationIndex - 1,
        0,
        movedArgument
      );
      return {
        ...state,
        InputArguments: newInputArguments.map((ia, idx) => ({
          ...ia,
          Order: idx,
        })),
      };
    },
    moveVariableGroup: (state, action) => {
      const { sourceIndex, destinationIndex } = action.payload;
      const newVariableGroups = [...state.VariableGroups];
      const [movedGroup] = newVariableGroups.splice(sourceIndex, 1);
      newVariableGroups.splice(
        sourceIndex > destinationIndex
          ? destinationIndex
          : destinationIndex - 1,
        0,
        movedGroup
      );
      return {
        ...state,
        VariableGroups: newVariableGroups.map((vg, idx) => ({
          ...vg,
          Order: idx,
        })),
      };
    },
    setVariableGroup: (state, action) => ({
      ...state,
      VariableGroups: state.VariableGroups.map((vg) => {
        if (vg.GroupUuid === action.payload.GroupUuid) {
          return action.payload;
        }
        return vg;
      }),
    }),
    deleteVariableGroup: (state, action) => ({
      ...state,
      VariableGroups: state.VariableGroups.filter(
        (vg) => vg.GroupUuid !== action.payload
      )
        .sort((a, b) => a.Order - b.Order)
        .map((vg, index) => ({ ...vg, Order: index })),
    }),
    addNewVariableGroup: (state, action) => {
      const newInputArgument = {
        GroupUuid: action.payload.GroupUuid,
        Label: "New variable group",
        Order: state.VariableGroups ? state.VariableGroups.length : 0,
        ...action.payload,
      };
      if (!state.VariableGroups) state.VariableGroups = [];
      state.VariableGroups.push(newInputArgument);
    },
    moveVariableGroupUp: (state, action) => {
      const index = state.VariableGroups.findIndex(
        (vg) => vg.GroupUuid === action.payload
      );
      if (index > 0) {
        const newVariableGroups = [...state.VariableGroups];
        [newVariableGroups[index - 1], newVariableGroups[index]] = [
          newVariableGroups[index],
          newVariableGroups[index - 1],
        ];
        return {
          ...state,
          VariableGroups: newVariableGroups.map((vg, idx) => ({
            ...vg,
            Order: idx,
          })),
        };
      }
    },
    moveVariableGroupDown: (state, action) => {
      const index = state.VariableGroups.findIndex(
        (vg) => vg.GroupUuid === action.payload
      );
      if (index < state.VariableGroups.length - 1) {
        const newVariableGroups = [...state.VariableGroups];
        [newVariableGroups[index + 1], newVariableGroups[index]] = [
          newVariableGroups[index],
          newVariableGroups[index + 1],
        ];
        return {
          ...state,
          VariableGroups: newVariableGroups.map((vg, idx) => ({
            ...vg,
            Order: idx,
          })),
        };
      }
    },
    setBusinessHoursList: (state, action) => ({
      ...state,
      BusinessHours: action.payload,
    }),
    setBusinessHours: (state, action) => ({
      ...state,
      BusinessHours: state.BusinessHours.map((bh) => {
        if (bh.UUID === action.payload.UUID) {
          return action.payload;
        }
        return bh;
      }),
    }),
    deleteBusinessHours: (state, action) => ({
      ...state,
      BusinessHours: state.BusinessHours.filter(
        (bh) => bh.UUID !== action.payload
      ),
    }),
    addNewBusinessHours: (state, action) => {
      const newBusinessHours = {
        UUID: action.payload.UUID,
        Label: action.payload.Label || "New business hours",
        TimeZone: "",
        Monday: {
          Business: "hours",
          Start: "08:00",
          End: "17:00",
        },
        Tuesday: {
          Business: "hours",
          Start: "08:00",
          End: "17:00",
        },
        Wednesday: {
          Business: "hours",
          Start: "08:00",
          End: "17:00",
        },
        Thursday: {
          Business: "hours",
          Start: "08:00",
          End: "17:00",
        },
        Friday: {
          Business: "hours",
          Start: "08:00",
          End: "17:00",
        },
        Saturday: {
          Business: "no-business",
        },
        Sunday: {
          Business: "no-business",
        },
      };
      state.BusinessHours.push(newBusinessHours);
    },
    setCalendars: (state, action) => ({
      ...state,
      Calendars: action.payload,
    }),
    setCalendar: (state, action) => ({
      ...state,
      Calendars: state.Calendars.map((bh) => {
        if (bh.UUID === action.payload.UUID) {
          return action.payload;
        }
        return bh;
      }),
    }),
    deleteCalendar: (state, action) => ({
      ...state,
      Calendars: state.Calendars.filter((bh) => bh.UUID !== action.payload),
    }),
    addNewCalendar: (state, action) => {
      const newCalendar = {
        UUID: action.payload,
        Label: "New calendar",
        Description: "My calendar 1",
        TotalCapacity: null,
        CapacityPerEvent: 10,
        CalendarEvents: [],
      };
      state.Calendars.push(newCalendar);
    },
  },
  extraReducers: (builder) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder.addCase(saveProject.fulfilled, (state, action) => {
      toast.success(`Saved "${state.Label}" successfully.`);
      return { ...initialState, ...action.payload };
    });
    builder.addCase(saveProject.pending, (state) => {
      toast.info(`Saving "${state.Label}".`);
    });
    builder.addCase(saveProject.rejected, (state, action) => {
      toast.error(`Problem saving "${state.Label}". ${action.payload}`);
    });
  },
});

export const {
  setProjectData,
  closeProject,
  setProjectMessageConfigUUID,
  setWorkflow,
  addNewWorkflow,
  setEntryWorkflow,
  deleteWorkflow,
  setEventHandler,
  setEventHandlers,
  editAction,
  setMessageTemplates,
  setMessageTemplate,
  deleteMessageTeplate,
  addNewMessageTemplate,
  setMessageConfigs,
  setMessageConfig,
  deleteMessageConfig,
  addNewMessageConfig,
  setInputArguments,
  setInputArgument,
  deleteInputArgument,
  addNewInputArgument,
  moveInputArgumentUp,
  moveInputArgumentDown,
  moveInputArgument,
  setBusinessHoursList,
  setBusinessHours,
  deleteBusinessHours,
  addNewBusinessHours,
  setCalendars,
  setCalendar,
  deleteCalendar,
  addNewCalendar,
  addNewVariableGroup,
  setVariableGroup,
  deleteVariableGroup,
  moveVariableGroupUp,
  moveVariableGroupDown,
  moveVariableGroup,
} = projectSlice.actions;

export default projectSlice.reducer;

export const selectProjectData = (state) => state.project;
export const selectProjectID = (state) => state.project.ID;
export const selectProjectUUID = (state) => state.project.UUID;
export const selectProjectLabel = (state) => state.project.Label;
export const selectProjectStatus = (state) => state.project.Status;
export const selectProjectMessageConfigUUID = (state) =>
  state.project.MessageConfigUUID;

export const selectWorkflows = (state) =>
  state.project.Workflows || EMPTY_ARRAY;
export const selectWorkflowsUUIDs = (state) =>
  JSON.stringify(state.project.Workflows?.map((wf) => wf.UUID));
export const selectWorkflow = (UUID) => (state) =>
  state.project.Workflows.find((wf) => wf.UUID === UUID);
export const selectParentWorkflows = (UUID) =>
  createSelector(
    [
      (state) => state.project.EventHandlers,
      (state) => state.project.Workflows,
    ],
    (eventHandlers, workflows) => {
      const parentEventHandlers = eventHandlers
        .filter((eh) =>
          eh.Rules?.some((r) =>
            r.Actions?.some(
              (a) => a.Type === "start:runtime" && a.TypeUUID === UUID
            )
          )
        )
        .map((eh) => eh.UUID);
      return workflows.filter((wf) =>
        wf.EventListeners?.some((el) =>
          parentEventHandlers.includes(el.EventHandlerUUID)
        )
      );
    }
  );

export const selectWorkflowEventListeners = (workflowUUID) => (state) => {
  const workflow = state.project.Workflows.find(
    (wf) => wf.UUID === workflowUUID
  );
  return workflow.EventListeners;
};

export const selectWorkflowEventListener =
  (workflowUUID, listenerUUID) => (state) => {
    const workflow = state.project.Workflows.find(
      (wf) => wf.UUID === workflowUUID
    );
    return workflow.EventListeners?.find((el) => el.UUID === listenerUUID);
  };

export const selectEventHandlers = (state) =>
  state.project.EventHandlers || EMPTY_ARRAY;
export const selectEventHandler = (UUID) => (state) =>
  state.project.EventHandlers.find((eh) => eh.UUID === UUID);

export const selectMessageTemplates = (state) =>
  state.project.MessageTemplates || EMPTY_ARRAY;
export const selectMessageTemplate = (UUID) => (state) =>
  state.project.MessageTemplates.find((mt) => mt.UUID === UUID);

export const selectMessageConfigs = (state) =>
  state.project.MessageConfigs || EMPTY_ARRAY;
export const selectMessageConfig = (UUID) => (state) =>
  state.project.MessageConfigs.find((mc) => mc.UUID === UUID);

export const selectInputArguments = (state) =>
  state.project.InputArguments || EMPTY_ARRAY;
export const selectInputArgument = (UUID) => (state) =>
  state.project.InputArguments?.find((ia) => ia.UUID === UUID);

export const selectVariableGroups = (state) =>
  state.project.VariableGroups || EMPTY_ARRAY;
export const selectVariableGroup = (UUID) => (state) =>
  state.project.VariableGroups?.find((vg) => vg.GroupUuid === UUID);

export const selectBusinessHoursList = (state) =>
  state.project.BusinessHours || EMPTY_ARRAY;
export const selectBusinessHours = (UUID) => (state) =>
  state.project.BusinessHours.find((bh) => bh.UUID === UUID);

export const selectCalendars = (state) =>
  state.project.Calendars || EMPTY_ARRAY;
export const selectCalendar = (UUID) => (state) =>
  state.project.Calendars.find((bh) => bh.UUID === UUID);

export const selectDataCategories = createSelector(
  [(state) => state.project.EventHandlers],
  (eventHandlers) => {
    const result = [];
    eventHandlers.forEach((eh) =>
      eh.Rules?.forEach((rule) =>
        rule.Actions?.forEach((action) =>
          action.Options?.forEach((opt) => {
            if (
              opt.ComponentConfig?.isDataCategory &&
              !opt.ComponentConfig?.isRawData
            ) {
              result.push(opt.Value);
            }
          })
        )
      )
    );
    return [...new Set(result)];
  }
);

export const selectRawDataCategories = createSelector(
  [(state) => state.project.EventHandlers],
  (eventHandlers) => {
    const result = [];
    eventHandlers.forEach((eh) =>
      eh.Rules?.forEach((rule) =>
        rule.Actions?.forEach((action) =>
          action.Options?.forEach((opt) => {
            if (
              opt.ComponentConfig?.isDataCategory &&
              opt.ComponentConfig?.isRawData
            ) {
              result.push(opt.Value);
            }
          })
        )
      )
    );
    return [...new Set(result)];
  }
);

export const selectParticipantCategories = createSelector(
  [(state) => state.project.EventHandlers],
  (eventHandlers) => {
    const result = [];
    eventHandlers.forEach((eh) =>
      eh.Rules?.forEach((rule) =>
        rule.Actions?.forEach((action) =>
          action.Options?.forEach((opt) => {
            if (opt.Type === "storageRecipient:category") {
              result.push(opt.Value);
            }
          })
        )
      )
    );
    return [...new Set(result)];
  }
);

export const selectCounterCategories = createSelector(
  [(state) => state.project.EventHandlers],
  (eventHandlers) => {
    const result = [];
    eventHandlers.forEach((eh) =>
      eh.Rules?.forEach((rule) =>
        rule.Actions?.forEach((action) =>
          action.Options?.forEach((opt) => {
            if (opt.Type === "counter:category") {
              result.push(opt.Value);
            }
          })
        )
      )
    );
    return [...new Set(result)];
  }
);

export const selectMilestoneLabels = createSelector(
  [(state) => state.project.EventHandlers],
  (eventHandlers) => {
    const result = [];
    eventHandlers.forEach((eh) =>
      eh.Rules?.forEach((rule) =>
        rule.Actions?.forEach((action) =>
          action.Options?.forEach((opt) => {
            if (
              opt.Type === "MilestoneLabel" ||
              opt.Type === "DsMilestoneLabel" ||
              opt.Type === "runtime-completion-milestone-label"
            ) {
              result.push(opt.Value);
            }
          })
        )
      )
    );
    return [...new Set(result)];
  }
);

export const selectProjectTimezone = (state) => state.project.Timezone;
